import { useEffect, useRef } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";

const COLLAPSED_ROW_HEIGHT = 51;
const EXPANDED_ROW_HEIGHT = 520;
const LOAD_MORE_THRESHOLD = 5;
const OVERSCAN = 8;
// ~half a second of frames, enough for the container height and the first
// measurement pass to settle.
const ANCHOR_SCROLL_ATTEMPTS = 30;

const useTaskRowVirtualizer = ({
	rows,
	scrollRef,
	expandedIds,
	anchorTaskId,
	onAnchorReached,
	hasNextPage,
	isFetchingNextPage,
	isFetchNextPageError,
	fetchNextPage,
}) => {
	const scrolledAnchorRef = useRef(null);
	const anchorFrameRef = useRef(null);
	const virtualizer = useVirtualizer({
		count: rows.length,
		getScrollElement: () => scrollRef.current,
		getItemKey: (index) => rows[index]?.id ?? index,
		estimateSize: (index) =>
			expandedIds.has(rows[index]?.original.id)
				? EXPANDED_ROW_HEIGHT
				: COLLAPSED_ROW_HEIGHT,
		measureElement: (element) => element?.getBoundingClientRect().height,
		overscan: OVERSCAN,
	});
	const virtualRows = virtualizer.getVirtualItems();
	const lastItem = virtualRows[virtualRows.length - 1];
	const lastVisibleIndex = lastItem?.index;

	useEffect(() => {
		if (!anchorTaskId || scrolledAnchorRef.current === anchorTaskId) return;
		const anchorIndex = rows.findIndex((row) => row.original.id === anchorTaskId);
		if (anchorIndex < 0) return;
		scrolledAnchorRef.current = anchorTaskId;
		onAnchorReached(anchorTaskId);
		// Retry across frames until the scroll actually takes.
		//
		// Two things defeat a single attempt. The container's height is applied on
		// a timer after mount, and scrolling a zero-height box silently does
		// nothing. Row sizes are also still being measured, so an early scroll gets
		// corrected back. The handle lives in a ref and is only cancelled on
		// unmount: cancelling from this effect's cleanup would kill it on the next
		// render, since the virtualiser is a new object every time.
		let attempts = 0;
		const scrollToAnchor = () => {
			const element = scrollRef.current;
			if (element?.clientHeight > 0) {
				virtualizer.scrollToIndex(anchorIndex, { align: "start" });
				if (anchorIndex === 0 || element.scrollTop > 0) return;
			}
			if (attempts++ < ANCHOR_SCROLL_ATTEMPTS) {
				anchorFrameRef.current = requestAnimationFrame(scrollToAnchor);
			}
		};
		anchorFrameRef.current = requestAnimationFrame(scrollToAnchor);
	}, [anchorTaskId, onAnchorReached, rows, scrollRef, virtualizer]);

	useEffect(
		() => () => {
			if (anchorFrameRef.current) cancelAnimationFrame(anchorFrameRef.current);
		},
		[]
	);

	useEffect(() => {
		if (
			lastVisibleIndex !== undefined &&
			lastVisibleIndex >= rows.length - LOAD_MORE_THRESHOLD &&
			hasNextPage &&
			!isFetchingNextPage &&
			!isFetchNextPageError
		) {
			fetchNextPage();
		}
	}, [
		fetchNextPage,
		hasNextPage,
		isFetchNextPageError,
		isFetchingNextPage,
		lastVisibleIndex,
		rows.length,
	]);

	return {
		virtualizer,
		virtualRows,
		topPadding: virtualRows[0]?.start ?? 0,
		bottomPadding: lastItem ? virtualizer.getTotalSize() - lastItem.end : 0,
	};
};

export default useTaskRowVirtualizer;
